import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Image,
  Upload,
  Folder,
  Grid,
  List,
  Search,
  MoreVertical,
  Trash2,
  Download,
  Tag,
  X,
  Plus,
  FileImage,
  FileVideo,
  File,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import DashboardLayout from '../components/DashboardLayout';
import { api, BACKEND_URL } from '../context/AuthContext';
import { toast } from 'sonner';

const FOLDERS = ['default', 'images', 'videos', 'logos', 'templates'];

const MediaLibraryPage = () => {
  const [media, setMedia] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [folderFilter, setFolderFilter] = useState('all');
  const [calendarFilter, setCalendarFilter] = useState('all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [uploadForm, setUploadForm] = useState({
    folder: 'default',
    calendar_id: 'none',
    tags: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mediaRes, calendarsRes] = await Promise.all([
        api.get('/media'),
        api.get('/calendars')
      ]);
      setMedia(mediaRes.data);
      setCalendars(calendarsRes.data);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'mp4', 'mov', 'avi', 'webm', 'mpeg'];
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const validateFile = (file) => {
    const ext = file.name.rsplit ? file.name.split('.').pop().toLowerCase() : (file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '');
    if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
      return `"${file.name}" has an unsupported format (.${ext || 'unknown'}). Allowed: JPG, PNG, GIF, WebP, SVG, MP4, MOV, AVI, WebM.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}" is too large (${(file.size / (1024*1024)).toFixed(1)}MB). Maximum size is 50MB.`;
    }
    if (file.size === 0) {
      return `"${file.name}" is empty.`;
    }
    return null;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    }
    
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
    }
    
    setSelectedFiles(validFiles);
    if (validFiles.length > 0) {
      setShowUploadDialog(true);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', uploadForm.folder);
        formData.append('tags', uploadForm.tags);
        if (uploadForm.calendar_id && uploadForm.calendar_id !== 'none') {
          formData.append('calendar_id', uploadForm.calendar_id);
        }

        const response = await api.post('/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMedia(prev => [response.data, ...prev]);
      }
      toast.success(`${selectedFiles.length} file(s) uploaded`);
      setShowUploadDialog(false);
      setSelectedFiles([]);
      setUploadForm({ folder: 'default', calendar_id: 'none', tags: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMedia) return;
    try {
      await api.delete(`/media/${selectedMedia.media_id}`);
      setMedia(media.filter(m => m.media_id !== selectedMedia.media_id));
      toast.success('Media deleted');
    } catch (error) {
      toast.error('Failed to delete media');
    } finally {
      setShowDeleteDialog(false);
      setSelectedMedia(null);
    }
  };

  const getFileIcon = (fileType) => {
    const ft = fileType || '';
    if (ft.startsWith('image/')) return FileImage;
    if (ft.startsWith('video/')) return FileVideo;
    return File;
  };

  const getItemFileType = (item) => item.file_type || item.content_type || '';
  const getItemFileSize = (item) => item.file_size || item.size || 0;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredMedia = media.filter(item => {
    if (folderFilter !== 'all' && item.folder !== folderFilter) return false;
    if (calendarFilter !== 'all' && item.calendar_id !== calendarFilter) return false;
    if (searchQuery && !item.filename.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    }
    
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
    }
    
    setSelectedFiles(validFiles);
    if (validFiles.length > 0) {
      setShowUploadDialog(true);
    }
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="media-library-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Image className="h-6 w-6 text-emerald-500" />
              Media Library
            </h1>
            <p className="text-slate-600 mt-1">{media.length} files</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="file"
              id="file-upload"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.mov,.avi,.webm,.mpeg"
              onChange={handleFileSelect}
              className="hidden"
            />
            <label htmlFor="file-upload">
              <Button
                asChild
                className="bg-emerald-500 hover:bg-emerald-600 text-white cursor-pointer"
              >
                <span data-testid="upload-media-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Media
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-100 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-media-input"
                  />
                </div>
              </div>
              <Select value={folderFilter} onValueChange={setFolderFilter}>
                <SelectTrigger className="w-[150px]">
                  <Folder className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Folders</SelectItem>
                  {FOLDERS.map((folder) => (
                    <SelectItem key={folder} value={folder}>
                      {folder.charAt(0).toUpperCase() + folder.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={calendarFilter} onValueChange={setCalendarFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Calendar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calendars</SelectItem>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.calendar_id} value={cal.calendar_id}>
                      {cal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex border border-slate-200 rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Media Grid/List */}
        {filteredMedia.length === 0 ? (
          <Card
            className="border-slate-100 border-2 border-dashed"
            onDrop={onDrop}
            onDragOver={onDragOver}
          >
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Image className="h-16 w-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {searchQuery || folderFilter !== 'all' ? 'No media found' : 'No media uploaded yet'}
              </h3>
              <p className="text-slate-500 text-center max-w-sm mb-6">
                Drag and drop files here or click the button below to upload
              </p>
              <label htmlFor="file-upload">
                <Button
                  asChild
                  className="bg-emerald-500 hover:bg-emerald-600 text-white cursor-pointer"
                >
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Media
                  </span>
                </Button>
              </label>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
            onDrop={onDrop}
            onDragOver={onDragOver}
          >
            {filteredMedia.map((item, i) => {
              const FileIcon = getFileIcon(getItemFileType(item));
              const isImage = getItemFileType(item).startsWith('image/');

              return (
                <motion.div
                  key={item.media_id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <Card className="border-slate-100 overflow-hidden group hover:border-emerald-200 transition-colors">
                    <div className="aspect-square relative bg-slate-100">
                      {isImage ? (
                        <img
                          src={`${BACKEND_URL}${item.url}`}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileIcon className="h-12 w-12 text-slate-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button size="icon" variant="secondary" className="h-8 w-8">
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="secondary"
                          className="h-8 w-8"
                          onClick={() => {
                            setSelectedMedia(item);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.filename}</p>
                      <p className="text-xs text-slate-500">{formatFileSize(getItemFileSize(item))}</p>
                      {item.tags && (Array.isArray(item.tags) ? item.tags : item.tags.split(',').filter(t => t.trim())).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(Array.isArray(item.tags) ? item.tags : item.tags.split(',').filter(t => t.trim())).slice(0, 2).map((tag, j) => (
                            <Badge key={j} variant="outline" className="text-xs px-1.5 py-0">
                              {typeof tag === 'string' ? tag.trim() : tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <Card className="border-slate-100">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-slate-600 text-sm">File</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600 text-sm">Folder</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600 text-sm">Size</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600 text-sm">Uploaded</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMedia.map((item) => {
                    const FileIcon = getFileIcon(getItemFileType(item));
                    return (
                      <tr key={item.media_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                              {getItemFileType(item).startsWith('image/') ? (
                                <img
                                  src={`${BACKEND_URL}${item.url}`}
                                  alt={item.filename}
                                  className="w-full h-full object-cover rounded"
                                />
                              ) : (
                                <FileIcon className="h-5 w-5 text-slate-400" />
                              )}
                            </div>
                            <span className="font-medium text-slate-900 truncate max-w-[200px]">
                              {item.filename}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{item.folder}</td>
                        <td className="py-3 px-4 text-slate-600">{formatFileSize(getItemFileSize(item))}</td>
                        <td className="py-3 px-4 text-slate-600">
                          {format(new Date(item.created_at), 'MMM d, yyyy')}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedMedia(item);
                                  setShowDeleteDialog(true);
                                }}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Media</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-900 mb-2">
                  {selectedFiles.length} file(s) selected
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedFiles.map((file) => (
                    <div key={file.name} className="flex items-center gap-2 text-sm text-slate-600">
                      <FileImage className="h-4 w-4" />
                      <span className="truncate">{file.name}</span>
                      <span className="text-slate-400">({formatFileSize(file.size)})</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Folder</Label>
                <Select
                  value={uploadForm.folder}
                  onValueChange={(value) => setUploadForm({ ...uploadForm, folder: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLDERS.map((folder) => (
                      <SelectItem key={folder} value={folder}>
                        {folder.charAt(0).toUpperCase() + folder.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Calendar (optional)</Label>
                <Select
                  value={uploadForm.calendar_id}
                  onValueChange={(value) => setUploadForm({ ...uploadForm, calendar_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select calendar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No calendar</SelectItem>
                    {calendars.map((cal) => (
                      <SelectItem key={cal.calendar_id} value={cal.calendar_id}>
                        {cal.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input
                  placeholder="product, summer, campaign"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Media</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedMedia?.filename}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default MediaLibraryPage;
